import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from '../../components/reports/DateRangePicker';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { AMAZON_FBA_TRANSFER_LEDGER } from '../../consts/reports';
import { FBA_TRANSFER_STATUS_LABELS } from '../fbaTransfers/FbaTransferStatusChip';
import { formatMoney, formatQty } from '../fbaTransfers/format';

const ymd = (date) => date.toISOString().slice(0, 10);
const MONEY = new Set(['unit_cost', 'extended_value']);
const QTY = new Set(['qty_planned', 'qty_removed', 'shiphero_before', 'shiphero_after', 'amazon_qty_shipped', 'amazon_qty_received', 'in_transit_qty', 'variance']);
const PAGE_SIZE = 1000;
const LEDGER_STATUSES = Object.entries(FBA_TRANSFER_STATUS_LABELS).filter(([value]) => !['DRAFT', 'CANCELLED'].includes(value));

export const useFbaTransferLedger = ({ startDate, endDate, status, enabled = true }) => {
  const [state, setState] = useState({ rows: [], columns: [], totals: null, totalRecords: 0, loading: false, error: null });
  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: null }));
    axios.get(`/api/v1/reports/${AMAZON_FBA_TRANSFER_LEDGER}`, {
      params: { startDate, endDate, status: status || undefined, pageSize: PAGE_SIZE },
      signal: controller.signal,
    })
      .then(({ data }) => setState({
        rows: data.data || [],
        columns: data.columns || [],
        totals: data.totals || null,
        totalRecords: data.totalRecords || 0,
        loading: false,
        error: null,
      }))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        setState((current) => ({ ...current, loading: false, error: error?.response?.data?.error || 'Failed to load the Amazon FBA transfer ledger' }));
      });
    return () => controller.abort();
  }, [startDate, endDate, status, enabled]);
  return state;
};

export const exportFbaTransferLedger = async ({ startDate, endDate, status }) => {
  const params = new URLSearchParams({ startDate, endDate });
  if (status) params.set('status', status);
  const response = await fetch(`/api/v1/reports/${AMAZON_FBA_TRANSFER_LEDGER}/export?${params.toString()}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (!response.ok) throw new Error('Failed to export the Amazon FBA transfer ledger');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = `amazon-fba-transfer-ledger-${startDate}_${endDate}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  return { handled: true };
};

export const FbaTransferStatusFilter = ({ value, onChange }) => (
  <select value={value} onChange={(event) => onChange(event.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
    <option value="">Active (hides Cancelled)</option>
    <option value="ALL">All statuses</option>
    {LEDGER_STATUSES.map(([status, label]) => <option key={status} value={status}>{label}</option>)}
    <option value="CANCELLED">Cancelled — No Movement</option>
    <option value="NON_AMAZON">Non-Amazon Inventory Corrections</option>
  </select>
);

const cell = (row, key) => {
  if (MONEY.has(key)) return formatMoney(row[key]);
  if (QTY.has(key)) return formatQty(row[key]);
  if (key === 'status') return FBA_TRANSFER_STATUS_LABELS[row.status] || row.status;
  return row[key] || (row[key] === 0 ? 0 : '—');
};

export const FbaTransferLedgerTable = ({ ledger }) => {
  const { rows, columns, totals, totalRecords, loading, error } = ledger;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4 text-sm text-gray-700">
        <span><strong>{formatQty(totals?.lines ?? rows.length)}</strong> lines</span>
        <span><strong>{formatQty(totals?.amazon_units_shipped ?? 0)}</strong> units Amazon shows shipped</span>
        <span><strong>{formatQty(totals?.units_removed ?? 0)}</strong> units left ShipHero</span>
        <span><strong>{formatMoney(totals?.extended_value ?? 0)}</strong> inventory value</span>
        <span><strong>{formatQty(totals?.in_transit_qty ?? 0)}</strong> units still in transit to Amazon</span>
      </div>
      {totalRecords > rows.length && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Showing the first {formatQty(rows.length)} of {formatQty(totalRecords)} lines. Totals cover every line; export the CSV for the full list.
        </div>
      )}
      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
      <div className="max-h-[70vh] overflow-auto rounded-lg border">
        <table className="min-w-full">
          <thead className="sticky top-0 z-[1] bg-gray-50">
            <tr className="border-b border-gray-200">
              {columns.map((col) => (
                <th key={col.key} className={`whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500 ${MONEY.has(col.key) || QTY.has(col.key) ? 'text-right' : 'text-left'}`}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={columns.length || 1}>Loading…</td></tr>}
            {!loading && !rows.length && <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={columns.length || 1}>No Amazon transfers in this date range.</td></tr>}
            {!loading && rows.map((row) => (
              <tr key={row.line_id}>
                {columns.map((col) => (
                  <td key={col.key} className={`whitespace-nowrap px-4 py-3 text-sm ${col.key === 'transfer_number' ? 'font-bold text-gray-900' : 'text-gray-900'} ${MONEY.has(col.key) || QTY.has(col.key) ? 'text-right tabular-nums' : ''} ${['exception_reason', 'lines_closed_reason'].includes(col.key) && row[col.key] ? 'text-red-700' : ''}`}>
                    {cell(row, col.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AmazonFbaTransferLedgerReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const today = useMemo(() => ymd(new Date()), []);
  const monthStart = useMemo(() => `${today.slice(0, 8)}01`, [today]);
  const { startDate, endDate, setDateRange } = useDateRangeParams(monthStart, today);
  const [status, setStatus] = useState('');
  const ledger = useFbaTransferLedger({ startDate, endDate, status });

  return (
    <ReportTemplate
      reportId={AMAZON_FBA_TRANSFER_LEDGER}
      title="Amazon FBA Transfer Ledger"
      description="Inventory that left ShipHero for Amazon FBA, valued at landed cost, with its Amazon shipment, in-transit and received quantities."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      filters={(
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker startDate={startDate} endDate={endDate} onDateChange={setDateRange} label="Transfer date" />
          <FbaTransferStatusFilter value={status} onChange={setStatus} />
        </div>
      )}
      exportButton={<CSVExportButton data={ledger.rows} columns={[]} filename="amazon-fba-transfer-ledger" onExport={() => exportFbaTransferLedger({ startDate, endDate, status })} />}
      isLoading={ledger.loading}
      renderChildrenOnly
    >
      <FbaTransferLedgerTable ledger={ledger} />
    </ReportTemplate>
  );
};

export default AmazonFbaTransferLedgerReport;
